<script>
export default {
  title: 'Project 3: Cat and Mouse',
  starterId: 'proj3.zip'
}
</script>

<template>
  <section>
    <Readings :readings="[
      {
        title: '<cite>Head First Java (2nd Edition)</cite> Chapter 7',
        link: 'http://proquest.safaribooksonline.com/book/programming/java/0596009208/7dot-inheritance-and-polymorphism-better-living-in-objectville/inheritance_and_polymorphism_colon_bette'
      },
      {
        title: '<cite>Head First Java (2nd Edition)</cite> Chapter 8',
        link: 'http://proquest.safaribooksonline.com/book/programming/java/0596009208/8dot-interfaces-and-abstract-classes-serious-polymorphism/interfaces_and_abstract_classes_colon_se'
      },
      {
        title: '<cite>Head First Java (2nd Edition)</cite> Chapter 9',
        link: 'http://proquest.safaribooksonline.com/book/programming/java/0596009208/9dot-constructors-and-garbage-collection-life-and-death-of-an-object/constructors_and_garbage_collection_colo'
      }
    ]"/>

    <article>
      <h2>Cat and Mouse</h2>

      <figure>
        <img src="./images/cat-and-mouse.png" alt="Cat and Mouse" class="img-responsive center-block" width="560">
        <figcaption>Figure 1. The Cat and Mouse problem.</figcaption>
      </figure>

      <p>The scene is an urban park; a cat watches a mouse run around the base of
        a statue of the actors from Monty Python. Over the course of a second, the
        somewhat witless mouse moves one meter counterclockwise around the
        statue&rsquo;s base, which is circular and two meters in diameter. Every
        second, the cat pursues the mouse as follows: If the cat can see the mouse,
        the cat moves one meter toward the statue. If the cat can&rsquo;t see the
        mouse, the cat circles 1.25 meters counterclockwise around the statue.</p>

      <p>The cat plans eventually to get close enough to the mouse to make it a
        juicy lunch. The mouse by accident, however, may manage to keep completely
        out of sight of the cat, since both the cat and the mouse are moving
        counter-clockwise.</p>

      <p>Should the chase go on more than 30 seconds, however,
        the cat will get tired and wander off.</p>
    </article>

    <article>
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above. It should contains 4 files
        called <code>CatMouseSimulation.java</code>, <code>Cat.java</code>,
        <code>Mouse.java</code> and <code>Position.java</code>.
      </p>
    </article>

    <article>
      <h2>Requirements</h2>

      <ul>
        <li>
          <p>Complete the classes to simulate this situation and determine if the cat
            catches the mouse, and provide additional code to test the program and its
            component classes. The main simulation instantiates a <dfn>Cat</dfn> object
            and a <dfn>Mouse</dfn> object, each having a <code>move</code> method.
            <span style="font-weight: bold">The cat moves first.</span> Your simulation
            should print out the results as nicely tabulated output. Here's an example
            interaction:</p>

          <pre v-highlightjs><code class="plain">$ javac CatMouseSimulation.java Position.java Cat.java Mouse.java
$ java CatMouseSimulation
Usage: java CatMouseSimulation &lt;catRadius&gt; &lt;catAngle&gt; &lt;mouseAngle&gt;
$ java CatMouseSimulation 3 0 0
Welcome to the CS9G cat and mouse simulation!
TIME  STATUS  MOUSE    CAT
0  init     (1.0,0.0)  (3.0,0.0)
1  running  (1.0,1.0)  (2.0,0.0)
2  running  (1.0,2.0)  (1.0,0.0)
3  running  (1.0,3.0)  (1.0,1.25)
4  running  (1.0,4.0)  (1.0,2.5)
5  running  (1.0,5.0)  (1.0,3.75)
6  running  (1.0,6.0)  (1.0,5.0)
7  eaten    (1.0,6.0)  (1.0,6.25)</code></pre>

        </li>

        <li>
          <p>
            <code>CatMouseSimulation.java</code> supplies empty <code>main()</code> and <code>runChase()</code>
            methods for you. The <code>main()</code> method should retrieve initial position values for the cat
            and the mouse from the user and initialize a cat and a mouse appropriately. It then calls
            <code>runChase()</code> to run the simulation to completion.
          </p>
        </li>
        <li>
          <p>
            <code>Position.java</code> defines the <dfn>Position</dfn> class. <code>Position</code>s are
            represented in polar coordinates. Each <code>Position</code> object consists of a
            <code>double</code> variable <code>myRadius</code>, and a <code>double</code> variable
            <code>myAngle</code>; both are private, and the <code>Position</code> class should not
            provide direct access to them. Methods provided for you in the <code>Position</code>
            class are several constructors, and <code>toString</code> (to allow positions to be printed).
            You are to supply an <code>update</code> method to increment the position&rsquo;s radius or angle.
            You will also need several comparison methods to allow a user to determine if a position is
            at the base of the statue, if it is at the same coordinate as another position, if its angle
            is between two other angles, and if a position on the base of the statue is in its line of
            sight. You will need a separate class, <code>PositionTestDrive</code>, to test the
            <code>Position</code> class. Write this yourself.
          </p>

        <li>
          <p>
            <code>Cat.java</code> and <code>Mouse.java</code> define the <dfn>Cat</dfn> and <dfn>Mouse</dfn>
            classes, respectively. Both have a <code>move</code> method and constructors. Neither class
            is allowed direct access to the components of a <code>Position</code>. The complete
            <code>Mouse</code> class is provided for you; you must provide the <code>move</code> method
            of the <code>Cat</code> class. The <code>Mouse</code> <code>move</code> method doesn't return
            anything. The <code>Cat</code> <code>move</code> method returns <code>true</code> if the cat
            eats the mouse during the move and returns <code>false</code> otherwise.
          </p>
        </li>

        <li>
          <p>Don&rsquo;t change the code provided for you; merely add methods and
            variables as specified above.</p>
        </li>

      </ul>
    </article>

    <article>
      <h2>Miscellaneous information</h2>

      <ul>
        <li>
          <p>
            In the framework code we provide and throughout the rest of the course material, you will encounter
            the <code>toString</code> method. This method by convention returns a printable version of whatever
            object it&rsquo;s applied to, and is provided with a number of Java&rsquo;s builtin classes.
          </p>
        </li>

        <li>
          <p>
            Your code should call <code>Position</code> methods where appropriate; code in <code>CatMouseSimulation</code>
            should not duplicate their functionality. You should not change the structure of the <code>Position</code>
            class; you may, however, provide additional methods.
          </p>
        </li>
        <li>
          <p>
            Don't get radians and degrees confused.
          </p>
        </li>
      </ul>
    </article>

    <article>
      <h2>Trigonometric Information</h2>
      <ul>
        <li>
          The cat sees the mouse if <br>
          <code>(cat radius) * cos (cat angle - mouse angle) >= 1.0</code> (angles in radian)
        </li>
        <li>
          When the cat circles distance d around the statue, its radius does not change, and the change in
          its angle can be calculated from the relationship <br>
          <code>d = angle * radius</code> (angles in radian)
        </li>
        <li>
          The cat catches the mouse when it (the cat) moves past the mouse while at the base of the statue,
          i.e. when the cat radius is 1.0 and the mouse angle lies between the old cat angle and the new
          cat angle. An angle B is between angles A and C in the following circumstances:<br>
          <code>cos (B - A) &gt; cos (C - A)</code>, and <code>cos (C - B) &gt; cos (C - A)</code> (angles in radian)<br>
          The difference C - A is assumed to be less than 90&deg;, or &pi;/2 radians.
        </li>
        <li>Note that the cat cannot move inside the statue&rsquo;s base; hence if the cat is, say, at radius 1.7 and sees the mouse, it can move in only 0.7 meters, up to the base of the statue.
        </li>
        <li>Remember that angles a, a + 2&pi;, a + 4&pi;, ... are all equal.</li>
      </ul>
    </article>

    <article>
      <h2>Testing</h2>

      <p>Test each function and method in your program with enough different
        inputs to show that it works. When testing a method, print out the input,
        the expected result, and the actual result.</p>

      <p>It would probably be helpful for debugging purposes to have a debug flag
        available that, when true, dumped the position and heading of all
        characters involved (cat and mouse) at each time step.</p>

      <p>You should test the methods of each Class in isolation, using
        appropriate test data, to see if they work independently before calling
        them from a driver function. Tutors may ask to see the results of these
        tests. Your test data should include the following values:</p>

      <p><i>(Note: Cat radius is calculated from the center of Statue)</i></p>

      <table class="table table-bordered table-striped">
        <tr>
          <th align="center">Cat Radius (m)</th>
          <th align="center">Cat Angle (degree)</th>
          <th align="center">Mouse Angle (degree)</th>
          <th align="center">Test Case Features</th>
        </tr>
        <tr>
          <td align="center">1.0</td>
          <td align="center">35.0&deg;</td>
          <td align="center">396.0&deg;</td>
          <td align="center">angle &gt; 360; mouse caught immediately</td>
        </tr>
        <tr>
          <td align="center">3.2</td>
          <td align="center">0.0&deg;</td>
          <td align="center">-57.0&deg;</td>
          <td align="center">negative angle;<br>
            cat radius has fractional part;<br>
            mouse caught at time 17</td>
        </tr>
        <tr>
          <td align="center">8.1</td>
          <td align="center">0.0&deg;</td>
          <td align="center">45.0&deg;</td>
          <td align="center"></td>
        </tr>
        <tr>
          <td align="center">8.1</td>
          <td align="center">150.0&deg;</td>
          <td align="center">240.0&deg;</td>
          <td align="center"></td>
        </tr>
        <tr>
          <td align="center">4.0</td>
          <td align="center">0.0&deg;</td>
          <td align="center">-57.0&deg;</td>
          <td align="center"></td>
        </tr>
      </table>
      <p>...plus the following cases:</p>
      <ul>
        <li>cat catches mouse at time 30;</li>
        <li>cat would catch mouse at time 31 if the simulation ran that long;</li>
        <li>whatever other values are necessary to ensure that all statements in your program have been executed at least once.</li>
      </ul>

      <p>Be sure to check that the output makes sense for these tests, since the output is what most tutors will check first. Students are sometimes embarrassed to have a tutor point out that their programs have the cat moving deeper and deeper inside the statue, or moving away from the mouse instead of approaching it.</p>

    </article>

    <article>
      <h2>Checklist</h2>
      <ul>
        <li>Correctly working code:
          <ul>
            <li>completion of the <code>Cat</code> class</li>
            <li>completion of the <code>Position</code> class</li>
            <li>completion of the <code>CatMouseSimulation</code> class</li>
          </ul>
        </li>
        <li>Sufficient testing, with output sufficient to verify test correctness:
          <ul>
            <li>tests on specified values for cat and mouse positions;</li>
            <li>evidence of independent tests of all methods you authored in each class</li>
            <li>tests sufficient to exercise all statements in the program.</li>
          </ul>
        </li>
        <li>Good style:
          <ul>
            <li>appropriate use of indenting and white space;</li>
            <li>variable and method names that reflect their use;</li>
            <li>informative comments at the head of each method;</li>
          </ul>
        </li>
      </ul>
    </article>
  </section>

</template>



// WEBPACK FOOTER //
// src/components/assignments/java/CatAndMouse.vue